import React from 'react';
import { useNavigate, useParams, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, MapPin, Clock, Phone, Truck, CheckCircle2, Package, FileText, Hash, NotebookPen, ArrowRight,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useConfirm } from '@/hooks/use-confirm';
import { toast } from '@/components/ui/toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { COMPLETE, nextStatusAction, statusBadgeClass } from './status';
import { fmtDate, fmtDateTime } from '@/lib/format-date';

interface Address {
  addressLine1?: string; addressLine2?: string; city?: string;
  stateProvince?: string; postalCode?: string; country?: string;
  latitude?: number | null; longitude?: number | null;
}
interface AssetSummary {
  id?: string;
  fleetNumber?: string | null;
  registrationNumber?: string | null;
  branch?: { name?: string | null } | null;
  vehicleGroup?: { group?: string | null } | null;
}
interface DispatchJob {
  id: string;
  jobNumber: string | null;
  hiddenDispatchNumber: string | null;
  customer: string | null;
  fleetNumber: string | null;
  jobDetails: string | null;
  info: string | null;
  start: string | null;
  end: string | null;
  phoneNumber: string | null;
  status: string | null;
  address: Address | null;
  asset?: AssetSummary | null;
  completionNotes?: string | null;
  enRouteAt?: string | null;
  arrivedAt?: string | null;
  completedAt?: string | null;
}
interface JobProduct {
  id: string;
  quantity: number | null;
  unit: string | null;
  catalogueItem?: { id?: string; itemCode?: string | null; description?: string | null } | null;
}

/** Compact elapsed string for two timestamps. Returns "—" when either is
 *  missing, so the caller can render "Drove —" gracefully when the driver
 *  skipped a status broadcast. */
function fmtElapsed(fromIso?: string | null, toIso?: string | null): string {
  if (!fromIso || !toIso) return '—';
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  // Force Pacific/Auckland so the driver-facing time matches the depot view.
  return new Date(iso).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Pacific/Auckland', hour12: false });
}
function fmtAddress(a: Address | null): string | null {
  if (!a) return null;
  const parts = [a.addressLine1, a.addressLine2, a.city, a.stateProvince, a.postalCode]
    .map((s) => (s ?? '').trim()).filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}
function mapsUrl(a: Address): string {
  if (a.latitude != null && a.longitude != null) {
    return `https://www.google.com/maps/search/?api=1&query=${a.latitude},${a.longitude}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fmtAddress(a) ?? '')}`;
}

export function MyDispatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const jobQueryKey = React.useMemo(() => ['/dispatch/jobs', id] as const, [id]);
  // Store the job object itself under this key — NOT the wrapping ApiResponse.
  // The admin board (schedule.tsx) caches the same key as the bare job, so
  // storing a different shape here poisoned that shared cache (and vice versa),
  // surfacing as an intermittent "Job not found" / empty edit modal. `?? null`
  // because react-query rejects an `undefined` result; a 404 → null → not-found.
  const { data: job, isLoading, error } = useQuery<DispatchJob | null>({
    queryKey: jobQueryKey,
    queryFn: async () => (await api<DispatchJob>(`/dispatch/jobs/${id}`)).data ?? null,
    enabled: !!id,
    // Mirror the list page: keep the driver's view fresh on its own, so a
    // dispatcher edit (address, items, reassign) shows up without manual
    // reload. Refetch is silent — no navigation, no scroll jump.
    refetchInterval: 60_000,
    // The poll pauses while the app is backgrounded, so on resume the page
    // would sit on stale data for up to a full tick. Override the app-wide
    // default and catch up the moment the driver comes back.
    refetchOnWindowFocus: true,
  });

  const { data: productsRes, isLoading: productsLoading } = useQuery<{ data?: JobProduct[] }>({
    queryKey: ['/dispatch-job-products', id],
    queryFn: async () => api<JobProduct[]>(`/dispatch-job-products?dispatchJob=${id}&limit=200`),
    enabled: !!id,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const [notes, setNotes] = React.useState('');
  // When the job loads or a fresh refetch lands, seed the textarea with any
  // existing completionNotes — but only on initial mount per job. We don't
  // overwrite the driver's in-progress typing on every 60s refetch.
  const seededRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (job && seededRef.current !== job.id) {
      seededRef.current = job.id;
      setNotes(job.completionNotes ?? '');
    }
  }, [job]);

  const statusMutation = useMutation({
    mutationFn: async (body: { status: string; completionNotes?: string | null }) => {
      const res = await api(`/dispatch/jobs/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      if (!res.success) throw new Error(res.error?.message ?? 'Failed to update job');
      return res;
    },
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: jobQueryKey });
      const previous = queryClient.getQueryData<DispatchJob | null>(jobQueryKey);
      if (previous) {
        const optimistic: DispatchJob = {
          ...previous,
          status: body.status,
          completionNotes: body.completionNotes ?? previous.completionNotes ?? null,
        };
        queryClient.setQueryData(jobQueryKey, optimistic);
      }
      return { previous };
    },
    onError: (err: Error, _body, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(jobQueryKey, ctx.previous);
      toast('Update failed', err.message, 'error');
      // Failure is the one case data-sync never reports — but the server may
      // have applied the PUT even though the client saw an error (timeout on
      // cellular). Re-read truth instead of trusting the rollback.
      queryClient.invalidateQueries({ queryKey: jobQueryKey });
      queryClient.invalidateQueries({ queryKey: ['/my-dispatch'] });
    },
    onSuccess: (_res, body) => {
      if (body.status === COMPLETE) toast('Marked as complete', undefined, 'success');
    },
    // Cache refresh (this job, the list, dispatcher board, other tabs) is
    // handled centrally by lib/data-sync.ts off the PUT; onError above
    // already rolled back the optimistic patch.
  });

  /** Advance the job to its next status. Only the terminal (Complete) hop
   *  asks for confirmation and includes the notes payload — En route /
   *  Arrived are single-tap broadcasts. */
  async function advanceStatus() {
    if (!job) return;
    const action = nextStatusAction(job.status);
    if (!action) return;
    if (action.terminal) {
      const ok = await confirm({
        title: 'Mark this job as complete?',
        description: job.customer ? `${job.customer} • ${fmtTime(job.start)}–${fmtTime(job.end)}` : undefined,
        confirmLabel: 'Mark Complete',
      });
      if (!ok) return;
      const trimmed = notes.trim();
      statusMutation.mutate({ status: COMPLETE, completionNotes: trimmed === '' ? null : trimmed });
      return;
    }
    statusMutation.mutate({ status: action.next });
  }

  const products = productsRes?.data ?? [];
  const isDone = job?.status === COMPLETE;
  const addr = fmtAddress(job?.address ?? null);

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 pb-32">
      {/* Header — back chevron + title strip. Sticks under the app shell header
          so a long detail page keeps "back" reachable while scrolling. */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back" className="h-10 w-10">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-semibold truncate">Job details</h1>
        {job && <StatusBadge status={job.status} />}
      </div>

      {isLoading && (
        <>
          <Card><CardContent className="p-4 space-y-3">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent></Card>
          <Card><CardContent className="p-4 space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </CardContent></Card>
        </>
      )}

      {error && (
        <Card><CardContent className="py-8 text-center text-sm text-destructive">
          Couldn't load the job. {(error as Error).message}
        </CardContent></Card>
      )}

      {!isLoading && !job && !error && (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
          Job not found. <Link to="/my-dispatch" className="text-primary underline">Back to list</Link>
        </CardContent></Card>
      )}

      {job && (
        <>
          <Card>
            <CardContent className="p-4 space-y-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Customer</div>
                <div className="text-lg font-semibold">{job.customer || '—'}</div>
              </div>

              {(job.jobNumber || job.hiddenDispatchNumber) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Hash className="h-4 w-4 shrink-0" />
                  <span>
                    {job.jobNumber && <>#{job.jobNumber}</>}
                    {job.jobNumber && job.hiddenDispatchNumber && ' · '}
                    {job.hiddenDispatchNumber && <>Dispatch {job.hiddenDispatchNumber}</>}
                  </span>
                </div>
              )}

              <div className="flex items-start gap-2 text-sm">
                <Clock className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                <div>
                  <div>{fmtDateTime(job.start)}</div>
                  <div className="text-muted-foreground text-xs">to {fmtDateTime(job.end)}</div>
                </div>
              </div>

              {/* Actual progress timeline — stamps come from the server on
                  status transitions. Each row only renders once stamped; an
                  empty timeline means the driver hasn't broadcast anything
                  yet. */}
              {(job.enRouteAt || job.arrivedAt || job.completedAt) && (
                <div className="rounded-md border bg-muted/30 p-3 space-y-2 text-sm">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Progress</div>
                  {job.enRouteAt && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">Started drive</span>
                      <span>{new Date(job.enRouteAt).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Pacific/Auckland', hour12: false })}</span>
                    </div>
                  )}
                  {job.arrivedAt && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">Arrived</span>
                      <span>
                        {new Date(job.arrivedAt).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Pacific/Auckland', hour12: false })}
                        {job.enRouteAt && <span className="text-muted-foreground ml-2 text-xs">drove {fmtElapsed(job.enRouteAt, job.arrivedAt)}</span>}
                      </span>
                    </div>
                  )}
                  {job.completedAt && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">Completed</span>
                      <span>
                        {new Date(job.completedAt).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Pacific/Auckland', hour12: false })}
                        {job.arrivedAt && <span className="text-muted-foreground ml-2 text-xs">on site {fmtElapsed(job.arrivedAt, job.completedAt)}</span>}
                      </span>
                    </div>
                  )}
                  {job.enRouteAt && job.completedAt && (
                    <div className="flex items-center justify-between gap-3 pt-1 border-t text-xs text-muted-foreground">
                      <span>Total elapsed</span>
                      <span>{fmtElapsed(job.enRouteAt, job.completedAt)}</span>
                    </div>
                  )}
                </div>
              )}

              {job.address && addr && (
                <a
                  href={mapsUrl(job.address)}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-start gap-2 text-sm text-primary hover:underline"
                >
                  <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                  <span className="break-words">{addr}</span>
                </a>
              )}

              {job.phoneNumber && (
                <a href={`tel:${job.phoneNumber}`} className="flex items-center gap-2 text-sm text-primary hover:underline">
                  <Phone className="h-4 w-4 shrink-0" />
                  <span>{job.phoneNumber}</span>
                </a>
              )}

              {(job.asset?.fleetNumber || job.asset?.registrationNumber || job.fleetNumber) && (
                <div className="flex items-start gap-2 text-sm">
                  <Truck className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                  <div className="space-y-0.5">
                    {(job.asset?.fleetNumber || job.fleetNumber) && (
                      <div>Fleet #{job.asset?.fleetNumber || job.fleetNumber}</div>
                    )}
                    {job.asset?.registrationNumber && (
                      <div className="text-muted-foreground text-xs">Rego {job.asset.registrationNumber}</div>
                    )}
                    {(job.asset?.branch?.name || job.asset?.vehicleGroup?.group) && (
                      <div className="text-muted-foreground text-xs">
                        {[job.asset?.branch?.name, job.asset?.vehicleGroup?.group].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {job.jobDetails && (
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FileText className="h-4 w-4 text-muted-foreground" /> Details
                </div>
                <p className="text-sm whitespace-pre-wrap break-words">{job.jobDetails}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Package className="h-4 w-4 text-muted-foreground" /> Catalogue items
                </div>
                <span className="text-xs text-muted-foreground">
                  {productsLoading ? '…' : `${products.length} item${products.length === 1 ? '' : 's'}`}
                </span>
              </div>
              {productsLoading ? (
                <div className="py-4 text-center text-sm text-muted-foreground">Loading items…</div>
              ) : products.length === 0 ? (
                <div className="py-4 text-center text-sm text-muted-foreground">No catalogue items for this job.</div>
              ) : (
                <ul className="divide-y -mx-1">
                  {products.map((p) => (
                    <li key={p.id} className="py-2 px-1 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {p.catalogueItem?.description || p.catalogueItem?.itemCode || 'Item'}
                        </div>
                        {p.catalogueItem?.description && p.catalogueItem?.itemCode && (
                          <div className="text-xs text-muted-foreground truncate">{p.catalogueItem.itemCode}</div>
                        )}
                      </div>
                      <div className="text-right text-sm whitespace-nowrap">
                        <span className="font-semibold">{p.quantity ?? 0}</span>
                        {p.unit && <span className="text-muted-foreground ml-1">{p.unit}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Completion notes — editable while the job is open; once Complete
              the saved value is shown read-only. Driver can add context for
              the dispatcher (delay, partial delivery, customer not on site). */}
          {!isDone ? (
            <Card>
              <CardContent className="p-4 space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium" htmlFor="completion-notes">
                  <NotebookPen className="h-4 w-4 text-muted-foreground" />
                  Notes for dispatcher
                  <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                </label>
                <textarea
                  id="completion-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything the dispatcher should know about this job?"
                  rows={3}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </CardContent>
            </Card>
          ) : job.completionNotes ? (
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <NotebookPen className="h-4 w-4 text-muted-foreground" />
                  Notes
                </div>
                <p className="text-sm whitespace-pre-wrap break-words text-foreground/80">{job.completionNotes}</p>
              </CardContent>
            </Card>
          ) : null}

          {(() => {
            const action = nextStatusAction(job.status);
            if (!action) return null;
            return (
              <div className="fixed bottom-0 inset-x-0 z-20 border-t bg-background/95 backdrop-blur px-4 py-3 md:static md:border-0 md:bg-transparent md:px-0 md:py-0 md:backdrop-blur-none">
                <div className="mx-auto max-w-2xl">
                  <Button
                    onClick={advanceStatus}
                    disabled={statusMutation.isPending}
                    className="w-full h-12 text-base"
                  >
                    {action.terminal
                      ? <CheckCircle2 className="h-5 w-5 mr-2" />
                      : <ArrowRight className="h-5 w-5 mr-2" />}
                    {statusMutation.isPending ? 'Updating…' : action.label}
                  </Button>
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <Badge variant="secondary">No status</Badge>;
  if (status === COMPLETE) return <Badge variant="success">Complete</Badge>;
  const cls = statusBadgeClass(status);
  if (cls) return <Badge className={cls}>{status}</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}
